import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import {
  AccountsGridGetRowsResponse,
  ColumnIndexes,
  ColumnKeys,
  DEFAULT_PAGE_SIZE,
  GetAccountsResponse,
} from '../types';
import {
  ColumnDetails,
  GetRowsRequest,
  GetSummaryRequest,
  GetSummaryResponse,
  SetFiltersRequest,
  UpdateSettingsRequest,
} from '../../../grid/types/common';
import { sanitizeObject } from '../../../utils/sanitization.utils';
import { Filter } from '../../../grid/gridFilters/types';
import { convertGridFiltersToExtJsFilters, getColumnKeyForFilter, isDefaultFilter } from '../../../grid/utils/filters';
import { DEFAULT_PAGE_NUMBER } from '../../../grid/gridView/types';
import { API_TYPE_ACCOUNTS } from '../../../webcomponents.constants';

export class AccountsGridDataProvider extends GridBaseDataProvider<ColumnIndexes, ColumnKeys> {
  private pageSize: number = DEFAULT_PAGE_SIZE;
  private cachedGetAccountsResponse: GetAccountsResponse;
  private readonly allColumnDetails: {};
  private columnNameFrontendToGzBackendForFilter = {};
  private columnNameFrontendToGzBackendForSorting = {};
  public companyFilterDataProvider = API_TYPE_ACCOUNTS;

  private readonly accountsViewStateName = 'accounts-accountsViewState';

  constructor(public dependencies: any, allColumnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>[]) {
    super();
    this.allColumnDetails = allColumnDetails.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.key]: curr,
      }),
      {}
    );
    this.setColumnNameFrontendToGzBackendForFilter();
    this.setColumnNameFrontendToGzBackendForSorting();
  }

  async getAccounts(
    page: number,
    limit: number,
    sort: string | null,
    dir: string | null,
    filters: Filter<ColumnKeys>[]
  ): Promise<GetAccountsResponse> {
    const convertedFiltersToGzBackend = this.getConvertedFrontendFiltersToGzBackendFilters(filters);

    return await ConsoleRequest.createRequest<GetAccountsResponse>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'Users',
      method: 'readRecords',
      payload: {
        page: page,
        limit: limit,
        sort: sort,
        dir: dir,
        filter: convertGridFiltersToExtJsFilters(convertedFiltersToGzBackend),
      },
    });
  }

  async getSummary(request: GetSummaryRequest<ColumnKeys>): Promise<GetSummaryResponse> {
    this.cachedGetAccountsResponse = await this.getAccounts(
      request.pageNumber ?? DEFAULT_PAGE_NUMBER,
      request.pageSize ?? DEFAULT_PAGE_SIZE,
      request.sortColumnName ? this.columnNameFrontendToGzBackendForSorting[request.sortColumnName] : null,
      request.sortDirection ?? null,
      request.filters
    );

    return sanitizeObject({
      summary: `${this.cachedGetAccountsResponse.total} items`,
      numItems: this.cachedGetAccountsResponse.total,
    });
  }

  async getRows(request: GetRowsRequest<ColumnIndexes, ColumnKeys>): Promise<AccountsGridGetRowsResponse> {
    request.pageSize = this.pageSize;

    if (!this.cachedGetAccountsResponse) {
      this.cachedGetAccountsResponse = await this.getAccounts(
        request.pageNumber ?? DEFAULT_PAGE_NUMBER,
        request.pageSize ?? DEFAULT_PAGE_SIZE,
        request.sortColumnName ? this.columnNameFrontendToGzBackendForSorting[request.sortColumnName] : null,
        request.sortDirection ?? null,
        request.filters,
      );
    }

    return {
      success: true,
      rawEvents: this.cachedGetAccountsResponse.data
    };

  }

  async updateSettings(request: UpdateSettingsRequest<ColumnIndexes>): Promise<{}> {
    this.saveViewState(request);
    return {};
  }

  async setFilters(request: SetFiltersRequest<ColumnKeys>): Promise<{} | undefined> {
    this.saveViewState(request);
    return;
  }

  private getConvertedFrontendFiltersToGzBackendFilters(filters: Filter<ColumnKeys>[]): Filter<ColumnKeys>[] {
    const convertedFiltersToGzBackend: Filter<ColumnKeys>[] = [];

    for (const filter of filters) {
      const columnKey: ColumnKeys = getColumnKeyForFilter(filter);
      const columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys> = this.allColumnDetails[columnKey];

      if (!columnDetails) {
        continue;
      }

      if (isDefaultFilter(filter, columnDetails) && columnDetails.key !== ColumnKeys.COMPANY) {
        continue;
      }

      convertedFiltersToGzBackend.push({
        [this.columnNameFrontendToGzBackendForFilter[columnKey] as ColumnKeys]: filter[columnKey],
      });
    }

    return convertedFiltersToGzBackend;
  }

  private setColumnNameFrontendToGzBackendForFilter(): void {
    if (this.dependencies.isCloud()) {
      this.columnNameFrontendToGzBackendForFilter = {
        [ColumnKeys.FULL_NAME]: 'profile.fullName',
        [ColumnKeys.EMAIL]: 'email',
        [ColumnKeys.STATUS]: 'active',
        [ColumnKeys.ROLE]: 'role.roleId',
        [ColumnKeys.TWO_FA]: 'twoFactorAuthentication.enabled',
        [ColumnKeys.PASSWORD_EXPIRATION]: 'passwordLifetime',
        [ColumnKeys.ACCOUNT_LOCKOUT]: 'accountLockdown.enabled',
      };

      if (this.dependencies.permissions.canHaveChildCompanies) {
        this.columnNameFrontendToGzBackendForFilter[ColumnKeys.COMPANY] = 'company.id';
      }

      return;
    }

    this.columnNameFrontendToGzBackendForFilter = {
      [ColumnKeys.USERNAME]: 'userName',
      [ColumnKeys.EMAIL]: 'email',
      [ColumnKeys.STATUS]: 'status',
      [ColumnKeys.ROLE]: 'role.roleId',
      [ColumnKeys.SERVICES]: 'serviceTargets.serviceId',
      [ColumnKeys.TWO_FA]: 'twoFactorAuthentication.enabled',
      [ColumnKeys.ACCESS_RULE]: 'securityGroupRule.name',
    };
  }

  private setColumnNameFrontendToGzBackendForSorting(): void {
    if (this.dependencies.isCloud()) {
      this.columnNameFrontendToGzBackendForSorting = {
        [ColumnKeys.FULL_NAME]: 'profile.fullName',
        [ColumnKeys.EMAIL]: 'email',
        [ColumnKeys.ROLE]: 'role.name',
        [ColumnKeys.TWO_FA]: 'twoFactorAuthentication.enabled',
        [ColumnKeys.PASSWORD_EXPIRATION]: 'passwordLifetime',
        [ColumnKeys.ACCOUNT_LOCKOUT]: 'accountLockdown.enabled',
      };

      if (this.dependencies.permissions.canHaveChildCompanies) {
        this.columnNameFrontendToGzBackendForSorting[ColumnKeys.COMPANY] = 'company.name';
      }

      return;
    }

    this.columnNameFrontendToGzBackendForSorting = {
      [ColumnKeys.USERNAME]: 'userName',
      [ColumnKeys.EMAIL]: 'email',
      [ColumnKeys.ROLE]: 'role.name',
      [ColumnKeys.TWO_FA]: 'twoFactorAuthentication.enabled',
      [ColumnKeys.ACCESS_RULE]: 'securityGroupRule.name',
    };
  }

  private saveViewState(request: SetFiltersRequest<ColumnKeys> | UpdateSettingsRequest<ColumnIndexes>): void {
    const newState = this.getViewState();

    for (const property in request) {
      if (request.hasOwnProperty(property) && request[property] !== undefined) {
        newState[property] = request[property];
      }
    }
    Ext.state.Manager.set(this.accountsViewStateName, newState);
  }

  public getViewState(): UpdateSettingsRequest<ColumnIndexes> & SetFiltersRequest<ColumnKeys> {
    return Ext.state.Manager.get(this.accountsViewStateName, {}) as UpdateSettingsRequest<ColumnIndexes> &
      SetFiltersRequest<ColumnKeys>;
  }

  shouldDataReloadOnResetView(): boolean {
    return true;
  }
}
